import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';

interface HttpOptions {
    headers?: {
        [header: string]: string | string[];
    };
    responseType?: 'text' | 'json' | 'blob' | 'ArrayBuffer';
    params?:
        | HttpParams
        | {
              [param: string]: string | string[];
          };
}

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private API_URL = '';

    constructor(private http: HttpClient) {
        // API_URL comes from environment.ts, and is an empty string if production/QA build.  By doing this, we are allowing simultaneous qa, staging, and production API urls as we dynamically generate it.
        this.API_URL = environment.API_URL.length > 0 ? environment.API_URL : this.generateProductionApiUrl();
    }

    public get apiUrl() {
        return this.API_URL;
    }

    private generateProductionApiUrl() {
        const hostname = window.location.hostname;
        const appReplacementRegex = /app|meeting/;
        const qaReplacementRegex = /qa[0-9]/;
        let baseUrl = 'https://api.calendarhero.com/';

        const hostNameParts = hostname.split('.'); // i.e meeting.calendarhero.com, app.calendarhero.com, something.something.calendarhero.com
        const rootDomain = hostNameParts[hostNameParts.length - 2]; // calendarhero
        const mainDomain = `${hostNameParts[hostNameParts.length - 2]}.${hostNameParts[hostNameParts.length - 1]}`; // calendarhero.com

        // For our typical app/meeting for prod & qa environments
        if (mainDomain === 'calendarhero.com') {
            baseUrl = `https://${_.replace(hostname, appReplacementRegex, 'api')}/`;
        }
        // For CName related testing for qa environments, calendarhero.co, calendarhero.to, workbetter.ai
        else if (rootDomain === 'calendarhero' || rootDomain === 'workbetter') {
            const qaEnv = hostname.match(/qa[0-9]/);
            if (qaEnv?.length) {
                baseUrl = `https://api-${qaEnv[0]}.calendarhero.com/`;
            } else {
                baseUrl = `https://api-qa.calendarhero.com/`;
            }
        }

        return baseUrl;
    }

    /** TODO: We should update this to take in a generic type in the future and error out when response does not match
     * TODO: Also investigate adding `retryWhen` to improve the experience for users
     */
    public get(path: string, options?: HttpOptions, hasBody?: boolean): Observable<any> {
        let httpOptions = {};

        if (options?.params) {
            httpOptions = { ...httpOptions, params: options.params };
        }

        if (options?.headers) {
            httpOptions = { ...httpOptions, headers: options.headers };
        }

        if (options?.responseType) [(httpOptions = { ...httpOptions, responseType: options.responseType })];

        const httpGet = Object.keys(httpOptions).length
            ? this.http.get(this.API_URL + path, httpOptions)
            : this.http.get(this.API_URL + path);

        return httpGet.pipe(
            map((response) => {
                const body = response;
                if (hasBody && !body) throw new Error('Body missing from response');
                return body;
            }),
            catchError(this.handleError)
        );
    }

    public getById(path: string, id: string): Observable<any> {
        return this.http.get(this.API_URL + path + id).pipe(
            map((response) => {
                const body = response;
                return body || {};
            }),
            catchError(this.handleError)
        );
    }

    public post(path: string, payload: any): Observable<any> {
        return this.http.post(this.API_URL + path, payload).pipe(
            map((response) => response),
            catchError(this.handleError)
        );
    }

    public put(path: string, idOrQuery: string, payload?: any): Observable<any> {
        return this.http.put(this.API_URL + path + idOrQuery, payload).pipe(
            map((response) => {
                return response;
            }),
            catchError(this.handleError)
        );
    }

    // TODO: We do not need a separate arg for id anymore, remove and refactor references
    public deleteById(path: string, id: string, payload?: any): Observable<any> {
        return this.http.request('delete', this.API_URL + path + id, { body: payload }).pipe(
            map((response) => {
                const body = response;
                return body || {};
            }),
            catchError(this.handleError)
        );
    }

    private handleError(error: HttpErrorResponse) {
        // TODO: Replace these console.errors and send to LogDNA or Rollbar instead
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            const errorMessage = error.error?.message || error.error;
            console.error(`Backend returned code ${error.status}: ${errorMessage}`);
            return throwError(`${errorMessage}`);
        }
        // throwError will emit the error back to the Observer
        // if the backend does not return an error, return this generic user-facing error message
        return throwError(
            'Something went wrong, but our team has been notified. Please email hello@calendarhero.com if you require assistance.'
        );
    }
}
